<template>
  <!-- MODAL DE CAMBIO DE CONTRASEÑA -->
  <div v-if="isOpen" class="fixed inset-0 z-70 overflow-y-auto bg-gray-900/60 backdrop-blur-sm">
    <!-- Overlay de fondo -->
    <div class="fixed inset-0 transition-opacity" @click="closeModal"></div>
    
    <!-- Container del modal -->
    <div class="fixed inset-0 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        
        <!-- HEADER DEL MODAL -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
            </div>
            <h2 class="text-lg font-semibold text-gray-900">Cambiar Contraseña</h2>
          </div>
          <button @click="closeModal" class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div class="p-6">
          <!-- Mensaje de información -->
          <div class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <p class="text-sm font-medium text-blue-800">Cambio de contraseña</p>
                <p class="text-xs text-blue-600 mt-1">Por seguridad, ingresa tu contraseña actual y define una nueva.</p>
              </div>
            </div>
          </div>

          <!-- Formulario -->
          <form @submit.prevent="handleSubmit" class="space-y-4">
            
            <!-- Contraseña Actual -->
            <div>
              <label for="currentPassword" class="block text-sm font-medium text-gray-700 mb-2">
                Contraseña Actual *
              </label>
              <div class="relative">
                <input
                  id="currentPassword"
                  v-model="formData.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  required
                  :disabled="isLoading"
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                  placeholder="Ingresa tu contraseña actual">
                
                <button
                  type="button"
                  @click="showCurrentPassword = !showCurrentPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  :disabled="isLoading">
                  <svg v-if="showCurrentPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Nueva Contraseña -->
            <div>
              <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-2">
                Nueva Contraseña *
              </label>
              <div class="relative">
                <input
                  id="newPassword"
                  v-model="formData.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  required
                  minlength="8"
                  :disabled="isLoading"
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                  placeholder="Mínimo 8 caracteres"
                  @input="validatePassword">
                
                <button
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  :disabled="isLoading">
                  <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
              </div>
              
              <!-- Indicador de fortaleza -->
              <div v-if="formData.newPassword" class="mt-2">
                <div class="flex items-center space-x-2">
                  <div class="flex-1 bg-gray-200 rounded-full h-1">
                    <div :class="[
                      'h-1 rounded-full transition-all duration-300',
                      passwordStrength.color
                    ]" :style="{ width: passwordStrength.width }"></div>
                  </div>
                  <span :class="['text-xs font-medium', passwordStrength.textColor]">
                    {{ passwordStrength.label }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Confirmar Contraseña -->
            <div>
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Nueva Contraseña *
              </label>
              <div class="relative">
                <input
                  id="confirmPassword"
                  v-model="formData.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  :disabled="isLoading"
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                  placeholder="Confirma tu nueva contraseña"
                  :class="{ 'border-red-300': passwordMismatch && formData.confirmPassword }"
                  @blur="checkPasswordMatch">
                
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  :disabled="isLoading">
                  <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
              </div>
              
              <p v-if="passwordMismatch && formData.confirmPassword" class="text-xs text-red-600 mt-1">
                Las contraseñas no coinciden
              </p>
            </div>

            <!-- Botones del formulario -->
            <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                @click="closeModal"
                :disabled="isLoading"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="isLoading || !isFormValid"
                class="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
                <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{{ isLoading ? 'Cambiando...' : 'Cambiar Contraseña' }}</span>
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue-sonner'

export default {
  name: 'ChangePassword',
  
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: null
    },
    userId: {
      type: [String, Number],
      default: null
    }
  },

  emits: ['close', 'success', 'error'],

  data() {
    return {
      isLoading: false,
      
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      
      // Estados de visibilidad de contraseñas
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      
      // Validación
      passwordMismatch: false
    }
  },

  computed: {
    /**
     * Verifica si el formulario es válido
     */
    isFormValid() {
      return this.formData.currentPassword.length > 0 &&
             this.formData.newPassword.length >= 8 &&
             this.formData.confirmPassword.length > 0 &&
             !this.passwordMismatch &&
             this.formData.newPassword === this.formData.confirmPassword
    },

    /**
     * Calcula la fortaleza de la contraseña
     */
    passwordStrength() {
      const password = this.formData.newPassword
      let score = 0
      
      if (password.length >= 8) score++
      if (/[a-z]/.test(password)) score++
      if (/[A-Z]/.test(password)) score++
      if (/[0-9]/.test(password)) score++
      if (/[^A-Za-z0-9]/.test(password)) score++
      
      const strengths = {
        0: { label: 'Muy débil', color: 'bg-red-500', textColor: 'text-red-600', width: '20%' },
        1: { label: 'Débil', color: 'bg-red-400', textColor: 'text-red-500', width: '25%' },
        2: { label: 'Regular', color: 'bg-yellow-500', textColor: 'text-yellow-600', width: '50%' },
        3: { label: 'Buena', color: 'bg-blue-500', textColor: 'text-blue-600', width: '75%' },
        4: { label: 'Fuerte', color: 'bg-green-500', textColor: 'text-green-600', width: '90%' },
        5: { label: 'Muy fuerte', color: 'bg-green-600', textColor: 'text-green-700', width: '100%' }
      }
      
      return strengths[score] || strengths[0]
    }
  },

  methods: {
    /**
     * Cierra el modal y resetea el formulario
     */
    closeModal() {
      this.resetForm()
      this.$emit('close')
    },

    /**
     * Resetea el formulario
     */
    resetForm() {
      this.formData = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
      this.passwordMismatch = false
    },

    /**
     * Valida la contraseña en tiempo real
     */
    validatePassword() {
      this.checkPasswordMatch()
    },

    /**
     * Verifica si las contraseñas coinciden
     */
    checkPasswordMatch() {
      if (this.formData.confirmPassword) {
        this.passwordMismatch = this.formData.newPassword !== this.formData.confirmPassword
      }
    },

    /**
     * Maneja el envío del formulario
     */
    async handleSubmit() {
      if (!this.isFormValid) {
        toast.info('Por favor, completa todos los campos correctamente')
        return
      }

      this.isLoading = true

      try {
        // Llamar al servicio de autenticación para cambiar la contraseña
        await this.changePasswordRequest()

        toast.success('¡Contraseña cambiada exitosamente! La próxima vez usa tu nueva contraseña.')
        // Cerrar modal después de un breve delay
        setTimeout(() => {
          this.closeModal()
        }, 2000)

      } catch (error) {
        toast.error(error.message || 'Error al cambiar la contraseña. Intenta nuevamente.')
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Realiza la llamada al backend para cambiar la contraseña
     */
    async changePasswordRequest() {
      try {
        //   OBTENER ID DEL USUARIO (prioritario desde prop, fallback desde currentUser)
        const userId = this.userId || this.currentUser?.id || this.currentUser?.pk
        
        if (!userId) {
          throw new Error('No se pudo identificar el usuario actual. Recarga la página e intenta nuevamente.')
        }

        //   USAR TU ENDPOINT ESPECÍFICO CON EL ID DEL USUARIO
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };

        const csrfToken = getCookie('csrftoken');
        const jwtToken = getCookie('iiotefintel_cookie');

        const baseURL = process.env.VUE_APP_API_URL || 'https://efibox.efintel.cl';

        const response = await fetch(`${baseURL}/usuarios/api/usuarios/${userId}/cambiar_password/`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken || '',
            ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {})
          },
          body: JSON.stringify({
            password_antigua: this.formData.currentPassword,  // Según tu API
            password: this.formData.newPassword,              // Según tu API
            password2: this.formData.confirmPassword          // Según tu API
          })
        })

        //   MANEJO ROBUSTO DE LA RESPUESTA
        const responseText = await response.text()

        if (!response.ok) {
          let errorData
          try {
            errorData = JSON.parse(responseText)
            toast.description('Error del backend:', errorData)
          } catch (parseError) {
            throw new Error(`Error del servidor (${response.status}): ${response.statusText}`)
          }
          
          // Manejo específico de errores de tu backend
          if (errorData.password_antigua) {
            throw new Error('La contraseña actual no es correcta')
          }
          if (errorData.password) {
            const passwordErrors = Array.isArray(errorData.password) ? errorData.password : [errorData.password]
            throw new Error(passwordErrors.join(', '))
          }
          if (errorData.password2) {
            throw new Error('Error en la confirmación de contraseña')
          }
          if (errorData.detail) {
            throw new Error(errorData.detail)
          }
          if (errorData.non_field_errors) {
            throw new Error(errorData.non_field_errors.join(', '))
          }
          
          throw new Error('Error del servidor al cambiar contraseña')
        }

        // Parsear respuesta exitosa
        let data
        try {
          data = JSON.parse(responseText)
        } catch (parseError) {
          // Si no es JSON válido pero la respuesta fue 200, asumir éxito
          data = { success: true, message: 'Contraseña cambiada exitosamente' }
        }

        console.log('  Contraseña cambiada exitosamente:', data)
        return data

      } catch (error) {
        console.error('❌ Error en changePasswordRequest:', error)
        throw error
      }
    },

    /**
     * Muestra mensaje de éxito
     */
    showSuccess(message) {
      this.$emit('success', message)
      // También mostrar alert si el padre no maneja el evento
      setTimeout(() => {
        alert(`  ${message}`)
      }, 100)
    },

    /**
     * Muestra mensaje de error
     */
    showError(message) {
      this.$emit('error', message)
      // También mostrar alert si el padre no maneja el evento  
      setTimeout(() => {
        alert(`❌ ${message}`)
      }, 100)
    }
  },

  watch: {
    /**
     * Resetear formulario cuando se abre el modal
     */
    isOpen(newValue) {
      if (newValue) {
        this.resetForm()
        // Focus en el primer input después de abrir
        this.$nextTick(() => {
          const firstInput = document.getElementById('currentPassword')
          if (firstInput) {
            firstInput.focus()
          }
        })
      }
    }
  },

  beforeUnmount() {
    // Limpiar cualquier timeout pendiente
    this.resetForm()
  }
}
</script>

<style scoped>
/* Transiciones para el modal */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Estilos para inputs con error */
.border-red-300:focus {
  border-color: rgb(252 165 165);
  box-shadow: 0 0 0 2px rgb(252 165 165, 0.5);
}
</style>